import type {
    CustomQuestionClientHistory,
    CustomQuestionConfirmation,
} from './CustomQuestionApi';

/**
 * Made-up data for the editor and the form designer, in the same shapes the
 *  server builds, so a question has something to render before it is on a
 *  real form.
 */

/**
 * What breely.confirmation looks like in preview, so a question written for a
 *  confirmation section has something to render in the editor and the form
 *  designer. Same shape the server builds in CustomQuestionConfirmation, and
 *  nothing in it is real - canceling one of these events in preview only
 *  changes this copy.
 */
export function sampleConfirmation(): CustomQuestionConfirmation {
    const start = new Date();
    start.setDate(start.getDate() + 3);
    start.setHours(10, 0, 0, 0);

    const end = new Date(start.getTime() + 60 * 60 * 1000);

    return {
        submission: {
            id: 0,
            formId: 0,
            formName: 'Booking form',
            submittedAt: new Date().toISOString(),
        },
        client: {
            firstName: 'Sam',
            lastName: 'Example',
            email: 'sam@example.com',
            phone: '555-0100',
        },
        answers: [
            {
                questionId: 1,
                label: 'Name',
                type: 'text',
                value: 'Sam Example',
                text: 'Sam Example',
            },
            {
                questionId: 2,
                label: 'Anything we should know?',
                type: 'bigtext',
                value: 'First visit',
                text: 'First visit',
            },
        ],
        events: [
            {
                id: 1,
                name: 'Consultation',
                category: null,
                description: 'A one hour consultation',
                start: start.toISOString(),
                end: end.toISOString(),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                isAllDay: false,
                isSeries: false,
                location: '123 Main St',
                resource: { id: 1, name: 'Alex' },
                price: 50,
                amountPaid: 50,
                amountOwed: 0,
                canceledAt: null,
                canCancel: true,
                canReschedule: true,
            },
        ],
        packages: [],
        giftCertificates: [],
    };
}

/** The sample with one event canceled, the way the server would report it */
export function withSampleEventCanceled(
    confirmation: CustomQuestionConfirmation,
    eventId: number,
): CustomQuestionConfirmation {
    return {
        ...confirmation,
        events: confirmation.events.map(event =>
            event.id === eventId
                ? {
                      ...event,
                      canceledAt: new Date().toISOString(),
                      canCancel: false,
                      canReschedule: false,
                  }
                : event,
        ),
    };
}

/**
 * What breely.getClientHistory() resolves with in preview: a past visit, the
 *  booking from the sample confirmation, a package and a paid invoice
 */
export function sampleClientHistory(): CustomQuestionClientHistory {
    const confirmation = sampleConfirmation();
    const upcoming = confirmation.events[0];

    const pastStart = new Date();
    pastStart.setDate(pastStart.getDate() - 30);
    pastStart.setHours(14, 0, 0, 0);

    const past = {
        ...upcoming,
        id: 2,
        start: pastStart.toISOString(),
        end: new Date(pastStart.getTime() + 60 * 60 * 1000).toISOString(),
        canCancel: false,
        canReschedule: false,
    };

    return {
        client: confirmation.client,
        events: [past, upcoming],
        packages: [
            {
                id: 1,
                name: '5 visit pass',
                isSubscription: false,
                status: 'active',
                remainingBalance: 3,
                expiresAt: null,
                nextRenewalAt: null,
            },
        ],
        invoices: [
            {
                id: 1,
                number: 1001,
                status: 'paid',
                total: 120,
                amountPaid: 120,
                sentAt: pastStart.toISOString(),
                dueAt: null,
            },
        ],
        waitlist: [],
    };
}
